import React from "react";
import {
  Box,
  Card,
  Typography,
  Grid2,
  Accordion,
  AccordionSummary,
  AccordionDetails,
  FormControl,
  TextField,
  Checkbox,
  Chip,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  Autocomplete,
  IconButton,
  Button,
} from "@mui/material";
import { ExpandMore as ExpandMoreIcon, Close as CloseIcon } from "@mui/icons-material";
import { LocalizationProvider } from "@mui/x-date-pickers/LocalizationProvider";
import { AdapterDayjs } from "@mui/x-date-pickers/AdapterDayjs";
import { DatePicker } from "@mui/x-date-pickers/DatePicker";
import dayjs from "dayjs";
import ClinicalExaminationToothChart from "./ClinicalExaminationToothChart";
import InvestigationToothChart from "./InvestigationtoothChart";
import { MedicineDurationOptions } from "../Utility/Constants";
import { saveFormDataToLocal } from "../Utility/FormDataTempStorage";

const treatmentOptions = [
  "Oral Prophylaxis",
  "RCT (Root Canal Treatment)",
  "Extraction",
  "Orthodontic Treatment",
  "Crown Implantation",
  "Pulpectomy",
  "Composite Restoration",
  "GIC (Glass Ionomer Cement) Restoration",
];

const medicalConditions = [
  "Diabetes",
  "Hypertension",
  "Cardiac Conditions",
  "Thyroid Disorders",
  "Asthma",
  "Allergies",
  "Others",
];

// ─── READ ONLY VIEW ───────────────────────────────────────────────────────────

const DentalReadOnlyView = ({ formData }) => {
  return (
    <Grid2 container spacing={2} sx={{ width: "100%" }}>

      {/* Clinical Examination */}
      <Grid2 size={12}>
        <Accordion defaultExpanded variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Clinical Examination</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {formData?.clinicalExamination?.length > 0 ? (
              <Card variant="outlined" sx={{ borderRadius: 2, p: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: "linear-gradient(90deg, #e3f2fd 0%, #f5f7fa 100%)" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>Tooth Number</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Condition</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.clinicalExamination.map((exam, index) => (
                      <TableRow key={index} hover>
                        <TableCell>{exam.toothNumber || "-"}</TableCell>
                        <TableCell>
                          {exam.toothCondition?.length > 0 ? (
                            <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                              {exam.toothCondition.map((c, i) => (
                                <Chip key={i} label={c} size="small" variant="outlined" />
                              ))}
                            </Box>
                          ) : "-"}
                        </TableCell>
                        <TableCell>{exam.toothNotes || "-"}</TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>
              </Card>
            ) : (
              <Typography sx={{ p: 2 }}>No clinical examination data recorded</Typography>
            )}
          </AccordionDetails>
        </Accordion>
      </Grid2>

      {/* Investigations */}
      <Grid2 size={12}>
        <Accordion defaultExpanded variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Investigations</Typography>
          </AccordionSummary>
          <AccordionDetails>
            {formData?.investigations?.length > 0 ? (
              <Card variant="outlined" sx={{ borderRadius: 2, p: 1 }}>
                <Table size="small">
                  <TableHead>
                    <TableRow sx={{ background: "linear-gradient(90deg, #e3f2fd 0%, #f5f7fa 100%)" }}>
                      <TableCell sx={{ fontWeight: "bold" }}>Tooth Number</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Investigation</TableCell>
                      <TableCell sx={{ fontWeight: "bold" }}>Notes</TableCell>
                    </TableRow>
                  </TableHead>
                  <TableBody>
                    {formData.investigations.map((inv, index) => {
                      const selected = inv.parameters?.filter(p => p.selected) || [];
                      return (
                        <TableRow key={index} hover>
                          <TableCell>{inv.toothNumber || "-"}</TableCell>
                          <TableCell>
                            {selected.length > 0 ? (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 1 }}>
                                {selected.map((param, i) => (
                                  <Box key={i}>
                                    <Chip label={param.displayValue} size="small" variant="outlined" sx={{ mb: 0.5 }} />
                                    {Array.isArray(param.subType) &&
                                      param.subType.filter(s => s.selected).length > 0 && (
                                        <Box sx={{ ml: 1, mt: 0.5 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            Types: {param.subType.filter(s => s.selected).map(s => s.displayValue).join(", ")}
                                          </Typography>
                                        </Box>
                                      )}
                                    {typeof param.subType === "object" &&
                                      !Array.isArray(param.subType) &&
                                      param.subType?.lateralCeph?.selected && (
                                        <Box sx={{ ml: 1, mt: 0.5 }}>
                                          <Typography variant="caption" color="text.secondary">
                                            + Lateral Cephalogram
                                          </Typography>
                                        </Box>
                                      )}
                                  </Box>
                                ))}
                              </Box>
                            ) : "-"}
                          </TableCell>
                          <TableCell>
                            {selected.length > 0 ? (
                              <Box sx={{ display: "flex", flexDirection: "column", gap: 0.5 }}>
                                {selected.map((param, i) =>
                                  param.notes ? (
                                    <Typography key={i} variant="body2" sx={{ fontSize: "0.75rem" }}>
                                      <strong>{param.displayValue}:</strong> {param.notes}
                                    </Typography>
                                  ) : null
                                ).filter(Boolean)}
                              </Box>
                            ) : "-"}
                          </TableCell>
                        </TableRow>
                      );
                    })}
                  </TableBody>
                </Table>
              </Card>
            ) : (
              <Typography sx={{ p: 2 }}>No investigations recorded</Typography>
            )}
          </AccordionDetails>
        </Accordion>
      </Grid2>

      {/* Treatment Advice */}
      <Grid2 size={12}>
        <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography fontWeight="bold" gutterBottom>Treatment Advice</Typography>
          <Box sx={{ background: "#fff", borderRadius: 2, p: 1 }}>
            {formData?.treatmentAdvice?.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {formData.treatmentAdvice.map((t, i) => (
                  <Chip key={i} label={t} variant="outlined" />
                ))}
              </Box>
            ) : (
              <Typography>No treatment advice recorded</Typography>
            )}
          </Box>
        </Card>
      </Grid2>

      {/* Past Medical History */}
      <Grid2 size={12}>
        <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography fontWeight="bold" gutterBottom>Past Medical History</Typography>
          <Box sx={{ background: "#fff", borderRadius: 2, p: 1 }}>
            {formData?.medicalHistory?.length > 0 ? (
              <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1 }}>
                {formData.medicalHistory.map((c, i) => (
                  <Chip key={i} label={c} variant="outlined" />
                ))}
              </Box>
            ) : (
              <Typography>No medical history recorded</Typography>
            )}
          </Box>
        </Card>
      </Grid2>

      {/* Medicines */}
      <Grid2 size={12}>
        <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography fontWeight="bold" gutterBottom>Medicines</Typography>
          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: "linear-gradient(90deg, #e3f2fd 0%, #f5f7fa 100%)" }}>
                <TableCell align="center" sx={{ fontWeight: "bold", width: 40 }}>#</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Medicine</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Frequency</TableCell>
                <TableCell sx={{ fontWeight: "bold" }}>Instructions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formData?.medicines?.length > 0 ? (
                formData.medicines.map((med) => (
                  <TableRow key={med.serialNo} hover>
                    <TableCell align="center">{med.serialNo}</TableCell>
                    <TableCell>{med.name || "-"}</TableCell>
                    <TableCell>{med.duration || "-"}</TableCell>
                    <TableCell>{med.frequency || "-"}</TableCell>
                    <TableCell>{med.instructions || "-"}</TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={5} align="center">No medicines prescribed</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </Grid2>

      {/* Additional Notes */}
      <Grid2 size={12}>
        <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography fontWeight="bold" gutterBottom>Additional Notes</Typography>
          <Typography sx={{ background: "#fff", borderRadius: 2, p: 1 }}>
            {formData?.additionalNotes || "No additional notes provided"}
          </Typography>
        </Card>
      </Grid2>

      {/* Follow Up Date */}
      <Grid2 size={12}>
        <Card variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
          <Typography fontWeight="bold" gutterBottom>Follow Up Date</Typography>
          <Typography sx={{ background: "#fff", borderRadius: 2, p: 1 }}>
            {formData?.followupDate || "No follow-up date scheduled"}
          </Typography>
        </Card>
      </Grid2>

    </Grid2>
  );
};

// ─── EDIT VIEW ────────────────────────────────────────────────────────────────

const DentalEditView = ({ uniqueId, formData, setFormData }) => {

  // Same pattern as FrozenShoulderPrescriptionDisplay
  const handleFormDataChange = (updatedFormData) => {
    saveFormDataToLocal(uniqueId, updatedFormData);
  };

  // Helper: update formData + save to local in one call
  const updateFormData = (updater) => {
    setFormData((prev) => {
      const updated = typeof updater === "function" ? updater(prev) : updater;
      saveFormDataToLocal(uniqueId, updated);
      return updated;
    });
  };

  return (
    <Grid2 container spacing={2} sx={{ width: "100%" }}>

      {/* Clinical Examination */}
      <Grid2 size={12}>
        <Accordion defaultExpanded variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Clinical Examination</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <ClinicalExaminationToothChart
              clinicalExamination={formData?.clinicalExamination || []}
              setClinicalExamination={(val) =>
                updateFormData((prev) => ({
                  ...prev,
                  clinicalExamination: typeof val === "function" ? val(prev.clinicalExamination) : val,
                }))
              }
            />
          </AccordionDetails>
        </Accordion>
      </Grid2>

      {/* Investigations */}
      <Grid2 size={12}>
        <Accordion defaultExpanded variant="outlined">
          <AccordionSummary expandIcon={<ExpandMoreIcon />}>
            <Typography fontWeight="bold">Investigation</Typography>
          </AccordionSummary>
          <AccordionDetails>
            <InvestigationToothChart
              investigations={formData?.investigations || []}
              setInvestigations={(val) =>
                updateFormData((prev) => ({
                  ...prev,
                  investigations: typeof val === "function" ? val(prev.investigations) : val,
                }))
              }
            />
          </AccordionDetails>
        </Accordion>
      </Grid2>

      {/* Treatment Advice */}
      <Grid2 size={12}>
        <Card variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
          <Typography fontWeight="bold" gutterBottom>Treatment Advice</Typography>
          <FormControl fullWidth>
            <Autocomplete
              multiple
              options={treatmentOptions}
              disableCloseOnSelect
              value={formData?.treatmentAdvice || []}
              onChange={(_, newValue) =>
                updateFormData((prev) => ({ ...prev, treatmentAdvice: newValue }))
              }
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} color="primary" />
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Treatment Procedures"
                  placeholder="Search and select treatments..."
                  variant="outlined"
                  sx={{ background: "#fff", borderRadius: 2 }}
                />
              )}
            />
          </FormControl>
        </Card>
      </Grid2>

      {/* Past Medical History */}
      <Grid2 size={12}>
        <Card variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
          <Typography fontWeight="bold" gutterBottom>Past Medical History</Typography>
          <FormControl fullWidth>
            <Autocomplete
              multiple
              options={medicalConditions}
              disableCloseOnSelect
              value={formData?.medicalHistory || []}
              onChange={(_, newValue) =>
                updateFormData((prev) => ({ ...prev, medicalHistory: newValue }))
              }
              renderOption={(props, option, { selected }) => (
                <li {...props}>
                  <Checkbox style={{ marginRight: 8 }} checked={selected} color="primary" />
                  {option}
                </li>
              )}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Select Applicable Medical Conditions"
                  placeholder="Search and select medical conditions..."
                  variant="outlined"
                  sx={{ background: "#fff", borderRadius: 2 }}
                />
              )}
            />
          </FormControl>
        </Card>
      </Grid2>

      {/* Medicines */}
      <Grid2 size={12}>
        <Card variant="outlined" sx={{ borderRadius: 2, p: 3 }}>
          <Box display="flex" justifyContent="space-between" alignItems="center" mb={2}>
            <Typography fontWeight="bold">Medicines</Typography>
            <Button
              variant="contained"
              color="primary"
              size="small"
              onClick={() =>
                updateFormData((prev) => ({
                  ...prev,
                  medicines: [...(prev.medicines || []), { serialNo: (prev.medicines?.length || 0) + 1 }],
                }))
              }
              sx={{ borderRadius: 2 }}
            >
              + Add Medicine
            </Button>
          </Box>

          <Table size="small">
            <TableHead>
              <TableRow sx={{ background: "linear-gradient(90deg, #e3f2fd 0%, #f5f7fa 100%)" }}>
                <TableCell align="center" sx={{ fontWeight: "bold", width: 60 }}>#</TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 200 }}>Medicine</TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 120 }}>Duration</TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 120 }}>Frequency</TableCell>
                <TableCell sx={{ fontWeight: "bold", minWidth: 180 }}>Instructions</TableCell>
                <TableCell sx={{ fontWeight: "bold", width: 60 }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {formData?.medicines?.length > 0 ? (
                formData.medicines.map((med) => (
                  <TableRow key={med.serialNo} hover>
                    <TableCell align="center">{med.serialNo}</TableCell>

                    <TableCell>
                      <TextField
                        fullWidth size="small" variant="outlined"
                        placeholder="Medicine name"
                        value={med.name || ""}
                        onChange={(e) =>
                          updateFormData((prev) => ({
                            ...prev,
                            medicines: prev.medicines.map((m) =>
                              m.serialNo === med.serialNo ? { ...m, name: e.target.value } : m
                            ),
                          }))
                        }
                      />
                    </TableCell>

                    <TableCell>
                      <Autocomplete
                        freeSolo
                        options={MedicineDurationOptions}
                        value={med.duration || ""}
                        onChange={(_, newValue) =>
                          updateFormData((prev) => ({
                            ...prev,
                            medicines: prev.medicines.map((m) =>
                              m.serialNo === med.serialNo ? { ...m, duration: newValue } : m
                            ),
                          }))
                        }
                        onInputChange={(_, newInputValue) =>
                          updateFormData((prev) => ({
                            ...prev,
                            medicines: prev.medicines.map((m) =>
                              m.serialNo === med.serialNo ? { ...m, duration: newInputValue } : m
                            ),
                          }))
                        }
                        renderInput={(params) => (
                          <TextField {...params} size="small" variant="outlined" placeholder="Duration" />
                        )}
                      />
                    </TableCell>

                    <TableCell>
                      <Autocomplete
                        freeSolo
                        options={["1-0-0", "1-0-1", "1-1-1", "0-0-1"]}
                        value={med.frequency || ""}
                        onChange={(_, newValue) =>
                          updateFormData((prev) => ({
                            ...prev,
                            medicines: prev.medicines.map((m) =>
                              m.serialNo === med.serialNo ? { ...m, frequency: newValue } : m
                            ),
                          }))
                        }
                        onInputChange={(_, newInputValue) =>
                          updateFormData((prev) => ({
                            ...prev,
                            medicines: prev.medicines.map((m) =>
                              m.serialNo === med.serialNo ? { ...m, frequency: newInputValue } : m
                            ),
                          }))
                        }
                        renderInput={(params) => (
                          <TextField {...params} size="small" variant="outlined" placeholder="Frequency" />
                        )}
                      />
                    </TableCell>

                    <TableCell>
                      <TextField
                        fullWidth size="small" variant="outlined"
                        placeholder="Instructions"
                        value={med.instructions || ""}
                        onChange={(e) =>
                          updateFormData((prev) => ({
                            ...prev,
                            medicines: prev.medicines.map((m) =>
                              m.serialNo === med.serialNo ? { ...m, instructions: e.target.value } : m
                            ),
                          }))
                        }
                      />
                    </TableCell>

                    <TableCell>
                      <IconButton
                        color="error" size="small"
                        onClick={() =>
                          updateFormData((prev) => {
                            const filtered = prev.medicines.filter((m) => m.serialNo !== med.serialNo);
                            return {
                              ...prev,
                              medicines: filtered.map((m, i) => ({ ...m, serialNo: i + 1 })),
                            };
                          })
                        }
                      >
                        <CloseIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))
              ) : (
                <TableRow>
                  <TableCell colSpan={6} align="center">No medicines prescribed</TableCell>
                </TableRow>
              )}
            </TableBody>
          </Table>
        </Card>
      </Grid2>

      {/* Additional Notes */}
      <Grid2 size={12}>
        <Typography fontWeight="bold" gutterBottom>Additional Notes</Typography>
        <TextField
          fullWidth multiline rows={3}
          value={formData?.additionalNotes || ""}
          onChange={(e) =>
            updateFormData((prev) => ({ ...prev, additionalNotes: e.target.value }))
          }
          variant="outlined"
          sx={{ background: "#fff", borderRadius: 2 }}
        />
      </Grid2>

      {/* Follow Up Date */}
      <Grid2 size={12}>
        <Typography fontWeight="bold" gutterBottom>Follow Up Date</Typography>
        <LocalizationProvider dateAdapter={AdapterDayjs}>
          <DatePicker
            value={formData?.followupDate ? dayjs(formData.followupDate) : null}
            onChange={(newDate) =>
              updateFormData((prev) => ({
                ...prev,
                followupDate: newDate ? newDate.format("DD-MMM-YYYY") : "",
              }))
            }
            format="DD-MMM-YYYY"
            slotProps={{
              textField: {
                size: "small",
                fullWidth: true,
                InputProps: {
                  sx: { background: "#fff", borderRadius: 2 },
                },
              },
            }}
          />
        </LocalizationProvider>
      </Grid2>

    </Grid2>
  );
};

// ─── MAIN EXPORT ─────────────────────────────────────────────────────────────

const DentalPrescriptionDisplay = ({ uniqueId, formData, readOnly, setFormData }) => {
  if (readOnly) {
    return <DentalReadOnlyView formData={formData} />;
  }
  return <DentalEditView uniqueId={uniqueId} formData={formData} setFormData={setFormData} />;
};

export default DentalPrescriptionDisplay;